const FALLBACK_COLOUR = "081E56";
const text = (value, fallback = "-") => {
  const result = String(value ?? "").trim();
  return result || fallback;
};
const colour = (value) => {
  const result = String(value || "").replace(/[^0-9a-f]/gi, "").toUpperCase();
  return result.length === 6 ? result : FALLBACK_COLOUR;
};
const safeFilePart = (value) =>
  text(value, "business-summary").replace(/[<>:"/\\|?*]+/g, "-").slice(0, 80);

const download = async (workbook, fileName) => {
  const buffer = await workbook.xlsx.writeBuffer();
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export async function exportBusinessSummary({
  rows,
  summary,
  categoryNames,
  brandName,
  primaryColor,
  incomePeriod,
  lastUpdated,
  search,
  filters,
}) {
  const excelModule = await import("exceljs");
  const ExcelJS = excelModule.default || excelModule;
  const workbook = new ExcelJS.Workbook();
  const brandColour = colour(primaryColor);
  const paleBrand = "E8EEF8";
  const generatedAt = new Date();
  workbook.creator = text(brandName, "Business Summary");
  workbook.created = generatedAt;
  workbook.modified = generatedAt;

  const entries = workbook.addWorksheet("Entries", {
    views: [{ state: "frozen", ySplit: 5 }],
  });
  const headers = [
    "Recipient",
    "Payment Created",
    "IOB Reference",
    "Selling Adviser",
    "Client",
    "Policy Number",
    "Provider",
    "Business Type",
    "Commission Type",
    "Payment Amount",
    "Split",
  ];
  entries.mergeCells("A1:K1");
  entries.getCell("A1").value = "Business Summary";
  entries.getCell("A1").font = { bold: true, size: 18, color: { argb: "FFFFFFFF" } };
  entries.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${brandColour}` } };
  entries.getCell("A1").alignment = { vertical: "middle" };
  entries.getRow(1).height = 30;
  entries.mergeCells("A2:K2");
  entries.getCell("A2").value = text(brandName, "Business Summary");
  entries.getCell("A2").font = { bold: true, color: { argb: `FF${brandColour}` } };
  entries.mergeCells("A3:K3");
  entries.getCell("A3").value = `Commission Month: ${incomePeriod || "Unavailable"}`;
  entries.getCell("A3").font = { italic: true, color: { argb: "FF555555" } };

  const headerRow = entries.getRow(5);
  headerRow.values = headers;
  headerRow.height = 24;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${brandColour}` } };
    cell.alignment = { vertical: "middle", wrapText: true };
  });

  rows.forEach((item, index) => {
    const row = entries.addRow([
      text(item.Recipient),
      text(item.PaymentCreated),
      text(item.IOBReference),
      text(item.PolicyAdviser),
      text(item.ClientName),
      text(item.PolicyAccountNumber),
      text(item.PolicyProvider),
      text(item.BusinessType),
      text(item.CommissionType),
      Number(item.PaymentAmount) || 0,
      Number(item.NormalisedSplit ?? item.Split) / 100 || 0,
    ]);
    if (index % 2) {
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${paleBrand}` } };
      });
    }
    if (item.ClientUrl) {
      row.getCell(5).value = { text: text(item.ClientName), hyperlink: item.ClientUrl };
      row.getCell(5).font = { color: { argb: `FF${brandColour}` }, underline: true };
    }
    if (item.PlanUrl) {
      row.getCell(3).value = { text: text(item.IOBReference), hyperlink: item.PlanUrl };
      row.getCell(3).font = { color: { argb: `FF${brandColour}` }, underline: true };
    }
    if (item.PlanUrl) {
      row.getCell(6).value = { text: text(item.PolicyAccountNumber), hyperlink: item.PlanUrl };
      row.getCell(6).font = { color: { argb: `FF${brandColour}` }, underline: true };
    }
    row.getCell(10).numFmt = '£#,##0.00;[Red]-£#,##0.00';
    row.getCell(11).numFmt = "0.####%";
    const paymentCreated = item.PaymentCreated ? new Date(item.PaymentCreated) : null;
    if (paymentCreated && !Number.isNaN(paymentCreated.getTime())) {
      row.getCell(2).value = paymentCreated;
      row.getCell(2).numFmt = "dd/mm/yyyy";
    }
    row.eachCell((cell) => {
      cell.alignment = { vertical: "top", wrapText: true };
      cell.border = { bottom: { style: "hair", color: { argb: "FFD0D0D0" } } };
    });
  });
  entries.autoFilter = { from: "A5", to: "K5" };
  entries.columns = [
    { width: 24 }, { width: 18 }, { width: 18 }, { width: 24 }, { width: 28 },
    { width: 24 }, { width: 22 }, { width: 22 }, { width: 20 }, { width: 18 }, { width: 12 },
  ];

  const stats = workbook.addWorksheet("Summary", {
    views: [{ state: "frozen", ySplit: 3 }],
  });
  stats.columns = [{ width: 30 }, { width: 34 }];
  stats.mergeCells("A1:B1");
  stats.getCell("A1").value = "Business Summary";
  stats.getCell("A1").font = { bold: true, size: 18, color: { argb: "FFFFFFFF" } };
  stats.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${brandColour}` } };
  stats.getRow(1).height = 30;
  stats.mergeCells("A2:B2");
  stats.getCell("A2").value = text(brandName, "Business Summary");
  stats.getCell("A2").font = { bold: true, color: { argb: `FF${brandColour}` } };

  const metadata = [
    ["Commission Month", incomePeriod || "Unavailable"],
    ["Last Updated", lastUpdated || "Unavailable"],
    ["Exported", generatedAt.toLocaleString("en-GB")],
    ["Search", search || "None"],
    ["Recipient Filter", filters.recipients?.join(", ") || "All"],
    ["Selling Adviser Filter", filters.policyAdvisers?.join(", ") || "All"],
    ["Commission Type Filter", filters.commissionTypes?.join(", ") || "All"],
    ["Business Category Filter", filters.businessCategories?.join(", ") || "All"],
  ];
  metadata.forEach(([label, value]) => stats.addRow([label, value]));
  stats.addRow([]);
  const section = stats.addRow(["Statistics", "Value"]);
  section.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${brandColour}` } };
  });
  const statisticRows = [
    ["Entries", rows.length],
    ["Total Payment", summary.total],
    ["New Business", summary.newBusiness],
    ["On-going", summary.ongoing],
    ...categoryNames
      .map((name) => [name, summary.categoryTotals[name] || 0])
      .filter(([, value]) => Number(value) !== 0),
    ...(Number(summary.unmappedCategory) !== 0
      ? [["Other", summary.unmappedCategory]]
      : []),
  ];
  statisticRows.forEach(([label, value], index) => {
    const row = stats.addRow([label, value]);
    if (index % 2) {
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: `FF${paleBrand}` } };
      });
    }
    if (label !== "Entries") row.getCell(2).numFmt = '£#,##0.00;[Red]-£#,##0.00';
  });

  const dateStamp = generatedAt.toISOString().slice(0, 10);
  await download(
    workbook,
    `${safeFilePart(brandName)}-business-summary-${dateStamp}.xlsx`,
  );
}
